import { useState } from 'react'
import { PenLine } from 'lucide-react'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { workChanged } from '../../hooks/useFresh.js'

/**
 * Send back, and Approve (or Mark reviewed, where a dossier takes three steps), for one document waiting on
 * this person -- a row of GET /approvals/. The same two buttons on the Approvals page and the front page,
 * so a document is decided the same way wherever it is met. Approving asks for the typed name, as the
 * dossier itself does; sending back asks for the reason.
 *
 *   onDone(text)   after it worked: the server's words, for the page's notice; the page reloads
 *   onError(text)  when it did not
 */
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail
  return typeof d === 'string' ? d : fallback
}

export default function DecideButtons({ row: r, onDone, onError }) {
  const { user } = useAuth()
  const [busy, setBusy] = useState(null)
  const [signing, setSigning] = useState(false)
  const [signature, setSignature] = useState('')
  const [signError, setSignError] = useState(null)   // shown in the signing window, not behind it
  const base = `/projects/${r.team_id}/dossiers/${r.dossier_id}/items/${r.item_id}`

  const act = async (key, fn) => {
    setBusy(key)
    try {
      const res = await fn()
      onDone?.(res?.data?.detail || 'Done.')
      workChanged()
      return true
    } catch (e) {
      onError?.(errText(e, 'That did not work.'))
      return false
    } finally { setBusy(null) }
  }

  const sign = async () => {
    setBusy('sign'); setSignError(null)
    try {
      const res = await api.post(`${base}/step`, { action: 'approve', signature })
      setSigning(false)
      onDone?.(res?.data?.detail || 'Approved.')
      workChanged()
    } catch (e) {
      setSignError(errText(e, 'That did not work.'))
    } finally { setBusy(null) }
  }

  const sendBack = () => {
    const reason = window.prompt(`Why is ${r.doc_number} being sent back?\n\nWhoever put it in is told, with this reason, and sends a corrected version.`)
    if (reason) act('sb', () => api.post(`${base}/send-back`, { reason }))
  }

  return (
    <>
      <button onClick={sendBack} disabled={!!busy}
              className="px-2.5 py-1 rounded-lg border border-red-300 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-40">
        Send back
      </button>
      {r.kind === 'review' ? (
        <button onClick={() => act('rv', () => api.post(`${base}/step`, { action: 'review' }))} disabled={!!busy}
                className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-40">
          Mark reviewed
        </button>
      ) : (
        <button onClick={() => { setSignature(''); setSignError(null); setSigning(true) }} disabled={!!busy}
                className="px-2.5 py-1 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 flex items-center gap-1 disabled:opacity-40">
          <PenLine className="w-3.5 h-3.5" /> Approve
        </button>
      )}

      {signing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSigning(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Approve {r.doc_number}</h3>
            <p className="text-sm text-gray-600 mt-1">
              “{r.title}” in {r.dossier_title}{r.final ? ', its final approval in the Submission folder' : ''}. To sign, type
              your full name as it appears on your account ({user?.full_name}).
            </p>
            <input value={signature} onChange={e => setSignature(e.target.value)} autoFocus
                   className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Your full name" />
            {signError && <p className="mt-2 text-sm text-red-600">{signError}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setSigning(false)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
              <button disabled={!signature.trim() || busy === 'sign'}
                      onClick={sign}
                      className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40">
                Approve & sign
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
